import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Mail, Lock, Phone , ChevronDown, Star, Shield, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "./context/AuthContext";
import AuthLayout from "./Auth/AuthLayout";
import GlassCard from "./Auth/GlassCard";
import { BASE_URL } from "./config/api";

import logo from "../images/logo.webp";

export default function CustomerLogin() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [showWarrantyCare, setShowWarrantyCare] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

  const sendOtp = async (isResend = false) => {
    if (!isValidEmail(email)) { setError("Enter a valid email address"); return; }
    try {
      setSendingOtp(true); setError("");
      const res = await axios.post(`${BASE_URL}warranty/customer/send-email-otp`, { email });
      if (res.data?.status) { setStep("otp"); setOtp(""); setCooldown(59); }
      else setError(res.data?.message || "Failed to send OTP");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP");
    } finally { setSendingOtp(false); }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 4) { setError("Enter valid OTP"); return; }
    try {
      setVerifyingOtp(true); setError("");
      const res = await axios.post(`${BASE_URL}warranty/customer/verify-email-otp`, { email, otp });
      if (res.data?.status) { login({ ...res.data.data, role: 7 }); navigate("/cust"); }
      else setError(res.data?.message || "Invalid OTP");
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed");
    } finally { setVerifyingOtp(false); }
  };

  return (
    <AuthLayout topLogo={logo}>
      <GlassCard>

        {/* ── LOGO + HEADER ── */}
        <div className="flex flex-col items-center mb-8">
          <div className="rounded-lg !border-none flex items-center justify-center mb-4">
            <img
              src={logo}
              alt="Logo"
              className="w-60 bg-transparent !border-none object-fit"
            />
          </div>
          <h2 className="text-[26px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Welcome Back!
          </h2>
          <p className="text-[12px] text-slate-400 font-medium mt-1.5 text-center">
            Login to explore and manage your warranties
          </p>
        </div>

        {/* ── EMAIL INPUT ── */}
        <div className="mb-5 group">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 ml-1">
            Email Address
          </label>
          <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50/60 px-4 focus-within:bg-white focus-within:border-green-500 focus-within:ring-4 focus-within:ring-green-500/10 transition-all duration-300 shadow-sm">
            <Mail
              className="text-slate-300 group-focus-within:text-green-500 transition-colors duration-300 mr-3 flex-shrink-0"
              size={18}
              strokeWidth={2}
            />
            <input
              value={email}
              disabled={step === "otp"}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full bg-transparent py-4 text-[14px] font-semibold outline-none text-slate-800 placeholder:text-slate-300 placeholder:font-normal disabled:opacity-50"
            />
          </div>
        </div>

        {/* ── OTP INPUT ── */}
        {step === "otp" && (
          <div className="mb-5 animate-in fade-in slide-in-from-top-2 duration-300 group">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 ml-1">
              Enter OTP
            </label>
            <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50/60 px-4 focus-within:bg-white focus-within:border-green-500 focus-within:ring-4 focus-within:ring-green-500/10 transition-all duration-300 shadow-sm">
              <Lock
                className="text-slate-300 group-focus-within:text-green-500 transition-colors duration-300 mr-3 flex-shrink-0"
                size={18}
                strokeWidth={2}
              />
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP"
                className="w-full bg-transparent py-4 text-[16px] font-bold outline-none text-slate-800 placeholder:text-slate-300 placeholder:font-normal tracking-[0.3em]"
              />
            </div>
            <div className="flex justify-between items-center mt-3 mx-1">
              <button
                onClick={() => { setStep("email"); setOtp(""); }}
                className="text-[11px] font-bold text-slate-400 hover:text-green-600 transition-colors"
              >
                ← Edit Email
              </button>
              <button
                onClick={() => sendOtp(true)}
                disabled={cooldown > 0 || sendingOtp}
                className="text-[11px] font-bold text-green-600 hover:text-green-700 disabled:text-slate-300 transition-colors"
              >
                {sendingOtp
                  ? "Sending..."
                  : cooldown > 0
                  ? `Resend in 00:${String(cooldown).padStart(2, "0")}`
                  : "Resend OTP"}
              </button>
            </div>
          </div>
        )}

        {/* ── ERROR ── */}
        {error && (
          <div className="mb-4 animate-in fade-in duration-300">
            <p className="text-[11px] font-semibold text-red-500 text-center bg-red-50 border border-red-100 py-2.5 rounded-xl">
              {error}
            </p>
          </div>
        )}

        {/* ── SUBMIT BUTTON ── */}
        <button
          onClick={step === "email" ? () => sendOtp(false) : verifyOtp}
          disabled={sendingOtp || verifyingOtp}
          className="w-full rounded-2xl py-4 mt-1 text-[13px] font-black uppercase tracking-widest text-white transition-all duration-300 disabled:opacity-60 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] bg-gradient-to-r from-green-600 to-emerald-500 shadow-[0_8px_24px_-6px_rgba(22,163,74,0.45)]"
        >
          {step === "email"
            ? sendingOtp ? "Sending OTP..." : "Continue with Email"
            : verifyingOtp ? "Verifying..." : "Verify & Login"}
        </button>

        {/* ── DIVIDER ── */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-slate-100" />
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
            Need Help?
          </span>
          <div className="flex-1 h-px bg-slate-100" />
        </div>

        {/* ── INDIA WARRANTY CARE EXPANDABLE ── */}
{/* ── INDIA WARRANTY CARE SECTION ── */}
<div className="mb-6">

  {/* ── ALWAYS VISIBLE: India #1 Badge ── */}
  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 p-4 shadow-lg shadow-green-200 mb-3">
    
    {/* Decorative circles */}
    <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-white/10" />
    <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/10" />

    {/* Top badge row */}
    <div className="flex items-center justify-between mb-3 relative z-10">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-yellow-400 rounded-full px-2.5 py-1">
          <Star size={9} className="text-yellow-800 fill-yellow-800" />
          <span className="text-[9px] font-black text-yellow-800 uppercase tracking-wide">
            #1 in India
          </span>
        </div>
        <div className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-1">
          <Shield size={9} className="text-white" />
          <span className="text-[9px] font-bold text-white uppercase tracking-wide">
            Trusted
          </span>
        </div>
      </div>
      {/* India flag text-based — works everywhere */}
     
    </div>

    {/* Main heading */}
    <p className="text-white font-black text-[20px] leading-tight tracking-tight relative z-10">
      India's <span className="text-yellow-300">#1</span> Warranty Service
    </p>

  

    {/* Click to call CTA inside the card */}
    <button
      type="button"
      onClick={() => setShowWarrantyCare((v) => !v)}
      className="flex items-center justify-center gap-2 w-full mt-3 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 active:bg-white/25 transition-all duration-200 relative z-10"
    >
      <Phone size={13} strokeWidth={2.5} className="text-white" />
      <span className="text-[11px] font-black text-white uppercase tracking-widest">
        {showWarrantyCare ? "Hide Contact" : "Contact Support"}
      </span>
      <ChevronDown
        size={13}
        strokeWidth={2.5}
        className={`text-white transition-transform duration-300 ${showWarrantyCare ? "rotate-180" : ""}`}
      />
    </button>
  </div>

  {/* ── EXPANDABLE: Warranty Care number — only shown on click ── */}
  {showWarrantyCare && (
    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
      <a
        href="tel:+918800000000"
        className="flex items-center gap-3 w-full px-4 py-3.5 rounded-2xl border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 transition-all duration-200 group"
      >
        {/* Flag circle */}
        <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-green-100 shadow-sm flex-shrink-0 text-[22px] leading-none">
          🇮🇳
        </div>

        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[9px] font-black text-green-600 uppercase tracking-[0.15em]">
            India Warranty Care
          </span>
          <span className="text-[17px] font-extrabold text-slate-900 tracking-wide leading-tight">
            +91 88000 00000
          </span>
          <span className="text-[10px] text-slate-400 font-medium">
            Mon – Sat &nbsp;•&nbsp; 9 AM – 7 PM IST
          </span>
        </div>

        {/* Animated call button */}
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-500 group-hover:bg-green-600 transition-colors flex-shrink-0 shadow-md shadow-green-200">
          <Phone size={16} strokeWidth={2.5} className="text-white" />
        </div>
      </a>

      {/* Tap to call hint */}
      <p className="text-center text-[9px] text-slate-300 font-medium mt-2">
        Tap the card to call directly
      </p>
    </div>
  )}

</div>

      

        {/* ── TERMS & PRIVACY ── */}
        <div className="flex flex-col items-center gap-2">

          <p className="text-[10.5px] text-slate-400 text-center leading-relaxed px-2">
            By continuing, you agree to our{" "}
            <br />
            <a
              href="/terms"
              className="font-bold text-green-600 hover:text-green-700 underline underline-offset-2 transition-colors"
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
             <a
              href="/privacy"
              className="font-bold text-green-600 hover:text-green-700 underline underline-offset-2 transition-colors"
            >
              Privacy Policy
            </a>
          </p>

          {/* Trust badges row */}
          {/* <div className="flex items-center gap-3 mt-1">
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Secure Login</span>
            </div>
            <div className="w-px h-3 bg-slate-200" />
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">256-bit SSL</span>
            </div>
            <div className="w-px h-3 bg-slate-200" />
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Verified</span>
            </div>
          </div> */}

        </div>

      </GlassCard>
    </AuthLayout>
  );
}
//****************************************************************************************************** */
// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail, Lock } from "lucide-react";
// import { useNavigate } from "react-router-dom";

// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// import { BASE_URL } from "./config/api";

// import logo from "../images/logo.webp";

// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email");
//   const [sendingOtp, setSendingOtp] = useState(false);
//   const [verifyingOtp, setVerifyingOtp] = useState(false);
//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   const sendOtp = async (isResend = false) => {
//     if (!isValidEmail(email)) { setError("Enter a valid email address"); return; }
//     try {
//       setSendingOtp(true); setError("");
//       const res = await axios.post(`${BASE_URL}warranty/customer/send-email-otp`, { email });
//       if (res.data?.status) { setStep("otp"); setOtp(""); setCooldown(59); }
//       else setError(res.data?.message || "Failed to send OTP");
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally { setSendingOtp(false); }
//   };

//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) { setError("Enter valid OTP"); return; }
//     try {
//       setVerifyingOtp(true); setError("");
//       const res = await axios.post(`${BASE_URL}warranty/customer/verify-email-otp`, { email, otp });
//       if (res.data?.status) { login({ ...res.data.data, role: 7 }); navigate("/cust"); }
//       else setError(res.data?.message || "Invalid OTP");
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally { setVerifyingOtp(false); }
//   };

//   return (
//     <AuthLayout topLogo={logo}>
//       <GlassCard>

//         {/* ── LOGO + HEADER ── */}
//         <div className="flex flex-col items-center mb-8">

//           {/* Logo box above text */}
//           <div className="rounded-lg !border-none   flex items-center justify-center mb-4">
//             <img
//               src={logo}
//               alt="Logo"
//               className="w-60 bg-transparent !border-none object-fit"
//               // If logo has dark bg, use: className="w-12 h-12 object-contain brightness-0 saturate-100 hue-rotate-[100deg]"
//             />
//           </div>

//           {/* Title */}
//           <h2 className="text-[26px] font-extrabold text-slate-900 tracking-tight leading-tight">
//             Welcome Back!
//           </h2>

//           {/* Subtitle */}
//           <p className="text-[12px] text-slate-400 font-medium mt-1.5 text-center">
//             Login to explore and manage your warranties
//           </p>
//         </div>

//         {/* ── EMAIL INPUT ── */}
//         <div className="mb-5 group">
//           <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 ml-1">
//             Email Address
//           </label>
//           <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50/60 px-4 focus-within:bg-white focus-within:border-green-500 focus-within:ring-4 focus-within:ring-green-500/10 transition-all duration-300 shadow-sm">
//             <Mail
//               className="text-slate-300 group-focus-within:text-green-500 transition-colors duration-300 mr-3 flex-shrink-0"
//               size={18}
//               strokeWidth={2}
//             />
//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="Enter your email"
//               className="w-full bg-transparent py-4 text-[14px] font-semibold outline-none text-slate-800 placeholder:text-slate-300 placeholder:font-normal disabled:opacity-50"
//             />
//           </div>
//         </div>

//         {/* ── OTP INPUT ── */}
//         {step === "otp" && (
//           <div className="mb-5 animate-in fade-in slide-in-from-top-2 duration-300 group">
//             <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 ml-1">
//               Enter OTP
//             </label>
//             <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50/60 px-4 focus-within:bg-white focus-within:border-green-500 focus-within:ring-4 focus-within:ring-green-500/10 transition-all duration-300 shadow-sm">
//               <Lock
//                 className="text-slate-300 group-focus-within:text-green-500 transition-colors duration-300 mr-3 flex-shrink-0"
//                 size={18}
//                 strokeWidth={2}
//               />
//               <input
//                 value={otp}
//                 onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
//                 placeholder="6-digit OTP"
//                 className="w-full bg-transparent py-4 text-[16px] font-bold outline-none text-slate-800 placeholder:text-slate-300 placeholder:font-normal tracking-[0.3em]"
//               />
//             </div>

//             <div className="flex justify-between items-center mt-3 mx-1">
//               <button
//                 onClick={() => { setStep("email"); setOtp(""); }}
//                 className="text-[11px] font-bold text-slate-400 hover:text-green-600 transition-colors"
//               >
//                 ← Edit Email
//               </button>
//               <button
//                 onClick={() => sendOtp(true)}
//                 disabled={cooldown > 0 || sendingOtp}
//                 className="text-[11px] font-bold text-green-600 hover:text-green-700 disabled:text-slate-300 transition-colors"
//               >
//                 {sendingOtp
//                   ? "Sending..."
//                   : cooldown > 0
//                   ? `Resend in 00:${String(cooldown).padStart(2, "0")}`
//                   : "Resend OTP"}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ── ERROR ── */}
//         {error && (
//           <div className="mb-4 animate-in fade-in duration-300">
//             <p className="text-[11px] font-semibold text-red-500 text-center bg-red-50 border border-red-100 py-2.5 rounded-xl">
//               {error}
//             </p>
//           </div>
//         )}

//         {/* ── SUBMIT BUTTON ── */}
//         <button
//           onClick={step === "email" ? () => sendOtp(false) : verifyOtp}
//           disabled={sendingOtp || verifyingOtp}
//           className="w-full rounded-2xl py-4 mt-1 text-[13px] font-black uppercase tracking-widest text-white transition-all duration-300 disabled:opacity-60 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] bg-gradient-to-r from-green-600 to-emerald-500 shadow-[0_8px_24px_-6px_rgba(22,163,74,0.45)]"
//         >
//           {step === "email"
//             ? sendingOtp ? "Sending OTP..." : "Continue with Email"
//             : verifyingOtp ? "Verifying..." : "Verify & Login"}
//         </button>

//       </GlassCard>
//     </AuthLayout>
//   );
// }
//************************************************************************************************* */
// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail, Lock } from "lucide-react";
// import { useNavigate } from "react-router-dom";

// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// import { BASE_URL } from "./config/api";

// import logo from "../images/logo.webp"; 

// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email");

//   const [sendingOtp, setSendingOtp] = useState(false);
//   const [verifyingOtp, setVerifyingOtp] = useState(false);

//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   const sendOtp = async (isResend = false) => {
//     if (!isValidEmail(email)) {
//       setError("Enter a valid email address");
//       return;
//     }
//     try {
//       setSendingOtp(true);
//       setError("");
//       const res = await axios.post(`${BASE_URL}warranty/customer/send-email-otp`, { email });
//       if (res.data?.status) {
//         setStep("otp");
//         setOtp("");
//         setCooldown(59);
//       } else {
//         setError(res.data?.message || "Failed to send OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setSendingOtp(false);
//     }
//   };

//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) {
//       setError("Enter valid OTP");
//       return;
//     }
//     try {
//       setVerifyingOtp(true);
//       setError("");
//       const res = await axios.post(`${BASE_URL}warranty/customer/verify-email-otp`, { email, otp });
//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Invalid OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally {
//       setVerifyingOtp(false);
//     }
//   };

//   return (
//     <AuthLayout topLogo={logo}>
//       <GlassCard>
        
//         {/* PREMIUM HEADER */}
//         <div className="flex flex-col items-center mb-8">

// <div className="rounded-2xl px-3 flex items-center justify-center mb-4 shadow-sm">
//             <img
//               src={logo}
//               alt="Logo"
//               className="w-280 object-contain"
//               // If logo has dark bg, use: className="w-12 h-12 object-contain brightness-0 saturate-100 hue-rotate-[100deg]"
//             />
//           </div>


//           <h2 className="text-[24px] font-extrabold text-slate-900 tracking-tight">
//             Welcome Back!
//           </h2>
//           <p className="text-xs text-slate-500 font-medium mt-1">
//             Login to explore and manage your warranties
//           </p>
//         </div>

//         {/* EMAIL INPUT */}
//         <div className="mb-6 group">
//           <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] mb-2 ml-1">
//             Email Address
//           </label>
//           <div className="relative flex items-center border border-slate-200/80 rounded-2xl bg-slate-50/50 px-4 focus-within:bg-white focus-within:border-[#15803d] focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all duration-300 shadow-sm">
//             <Mail className="text-slate-400 group-focus-within:text-[#16a34a] transition-colors duration-300 mr-3" size={18} strokeWidth={2} />
//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="Enter your email"
//               className="w-full bg-transparent py-4 text-[14px] font-semibold outline-none text-slate-800 placeholder:text-slate-400 placeholder:font-medium disabled:opacity-60"
//             />
//           </div>
//         </div>

//         {/* OTP INPUT */}
//         {step === "otp" && (
//           <div className="mb-6 animate-in fade-in slide-in-from-top-2 duration-300 group">
//             <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.15em] mb-2 ml-1">
//               Enter OTP
//             </label>
//             <div className="relative flex items-center border border-slate-200/80 rounded-2xl bg-slate-50/50 px-4 focus-within:bg-white focus-within:border-[#15803d] focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all duration-300 shadow-sm">
//               <Lock className="text-slate-400 group-focus-within:text-[#16a34a] transition-colors duration-300 mr-3" size={18} strokeWidth={2} />
//               <input
//                 value={otp}
//                 onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
//                 placeholder="6-digit OTP"
//                 className="w-full bg-transparent py-4 text-[15px] font-bold outline-none text-slate-800 placeholder:text-slate-400 placeholder:font-medium tracking-[0.25em]"
//               />
//             </div>

//             {/* OTP ACTIONS */}
//             <div className="flex justify-between items-center mt-3 mx-2">
//               <button
//                 onClick={() => {
//                   setStep("email");
//                   setOtp("");
//                 }}
//                 className="text-[11px] font-bold text-slate-400 hover:text-[#15803d] transition-colors"
//               >
//                 ← Edit Email
//               </button>

//               <button
//                 onClick={() => sendOtp(true)}
//                 disabled={cooldown > 0 || sendingOtp}
//                 className="text-[11px] font-bold text-[#16a34a] hover:text-[#15803d] disabled:text-slate-400 transition-colors"
//               >
//                 {sendingOtp
//                   ? "Sending..."
//                   : cooldown > 0
//                   ? `Resend in 00:${String(cooldown).padStart(2, "0")}`
//                   : "Resend OTP"}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ERROR MESSAGE */}
//         {error && (
//           <div className="mb-5 animate-in fade-in duration-300">
//             <p className="text-[11px] font-bold text-red-600 text-center bg-red-50 border border-red-100 py-2.5 rounded-xl">
//               {error}
//             </p>
//           </div>
//         )}

//         {/* TACTILE GRADIENT BUTTON */}
//         <button
//           onClick={step === "email" ? () => sendOtp(false) : verifyOtp}
//           disabled={sendingOtp || verifyingOtp}
//           className="w-full rounded-2xl py-4 mt-2 text-[13px] font-black uppercase tracking-wider text-white transition-all duration-300 disabled:opacity-70 hover:-translate-y-0.5 active:translate-y-0 bg-gradient-to-r from-[#16a34a] to-[#10b981] shadow-[0_8px_20px_-6px_rgba(22,163,74,0.5)]"
//         >
//           {step === "email" 
//             ? (sendingOtp ? "Sending..." : "Continue with Email") 
//             : (verifyingOtp ? "Verifying..." : "Verify & Login")
//           }
//         </button>

//       </GlassCard>
//     </AuthLayout>
//   );
// }
//********************************************************************************************************* */
// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail, Lock } from "lucide-react";
// import { useNavigate } from "react-router-dom";

// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// import { BASE_URL } from "./config/api";

// // Ensure this path matches where your image actually lives
// import logo from "../../src/images/logo.webp"; 

// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email");

//   const [sendingOtp, setSendingOtp] = useState(false);
//   const [verifyingOtp, setVerifyingOtp] = useState(false);

//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   /* ---------------- TIMER ---------------- */
//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   /* ---------------- SEND OTP ---------------- */
//   const sendOtp = async (isResend = false) => {
//     if (!isValidEmail(email)) {
//       setError("Enter a valid email address");
//       return;
//     }

//     try {
//       setSendingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/send-email-otp`,
//         { email }
//       );

//       if (res.data?.status) {
//         setStep("otp");
//         setOtp("");
//         setCooldown(59);
//       } else {
//         setError(res.data?.message || "Failed to send OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setSendingOtp(false);
//     }
//   };

//   /* ---------------- VERIFY OTP ---------------- */
//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) {
//       setError("Enter valid OTP");
//       return;
//     }

//     try {
//       setVerifyingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/verify-email-otp`,
//         { email, otp }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Invalid OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally {
//       setVerifyingOtp(false);
//     }
//   };

// return (
//     <AuthLayout topLogo={logo}>
//       <GlassCard>
        
//         {/* MODERN HEADER */}
//         <div className="flex flex-col items-center mb-8">
//           <h2 className="text-[26px] font-black text-slate-900 tracking-tight">
//             Welcome Back!
//           </h2>
//           <p className="text-xs text-slate-500 font-medium mt-1.5">
//             Login to explore and manage your warranties
//           </p>
//         </div>

//         {/* EMAIL INPUT */}
//         <div className="mb-5 group">
//           <label className="block text-[11px] font-bold text-slate-700 mb-1.5 ml-1 uppercase tracking-wider">
//             Email Address
//           </label>
//           <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50 px-4 focus-within:border-[#15803d] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all duration-300">
//             <Mail className="text-slate-400 group-focus-within:text-[#16a34a] transition-colors duration-300 mr-3" size={18} />
//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="Enter your email"
//               className="w-full bg-transparent py-4 text-[14px] font-medium outline-none placeholder:text-slate-400 disabled:opacity-60 text-slate-800"
//             />
//           </div>
//         </div>

//         {/* OTP INPUT */}
//         {step === "otp" && (
//           <div className="mb-5 animate-in fade-in slide-in-from-top-2 duration-300 group">
//             <label className="block text-[11px] font-bold text-slate-700 mb-1.5 ml-1 uppercase tracking-wider">
//               Enter OTP
//             </label>
//             <div className="relative flex items-center border border-slate-200 rounded-2xl bg-slate-50 px-4 focus-within:border-[#15803d] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all duration-300">
//               <Lock className="text-slate-400 group-focus-within:text-[#16a34a] transition-colors duration-300 mr-3" size={18} />
//               <input
//                 value={otp}
//                 onChange={(e) =>
//                   setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
//                 }
//                 placeholder="6-digit OTP"
//                 className="w-full bg-transparent py-4 text-[14px] font-bold outline-none placeholder:text-slate-400 placeholder:font-medium text-slate-800 tracking-[0.2em]"
//               />
//             </div>

//             {/* OTP ACTIONS */}
//             <div className="flex justify-between items-center mt-3 mx-2">
//               <button
//                 onClick={() => {
//                   setStep("email");
//                   setOtp("");
//                 }}
//                 className="text-[11px] font-bold text-slate-400 hover:text-[#15803d] transition-colors"
//               >
//                 ← Edit Email
//               </button>

//               <button
//                 onClick={() => sendOtp(true)}
//                 disabled={cooldown > 0 || sendingOtp}
//                 className="text-[11px] font-bold text-[#16a34a] hover:text-[#15803d] disabled:text-slate-400 transition-colors"
//               >
//                 {sendingOtp
//                   ? "Sending..."
//                   : cooldown > 0
//                   ? `Resend in 00:${String(cooldown).padStart(2, "0")}`
//                   : "Resend OTP"}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ERROR MESSAGE */}
//         {error && (
//           <div className="mb-4 animate-in fade-in duration-300">
//             <p className="text-[11px] font-bold text-red-600 text-center bg-red-50 border border-red-100 py-2.5 rounded-xl">
//               {error}
//             </p>
//           </div>
//         )}

//         {/* PRIMARY BUTTON */}
//         <button
//           onClick={step === "email" ? () => sendOtp(false) : verifyOtp}
//           disabled={sendingOtp || verifyingOtp}
//           className="w-full rounded-2xl py-4 font-bold text-[14px] text-white transition-all duration-300 disabled:opacity-70 mt-2 hover:-translate-y-0.5 active:translate-y-0"
//           style={{ 
//             background: "linear-gradient(135deg, #16a34a, #15803d)", 
//             boxShadow: "0 8px 25px rgba(22, 163, 74, 0.3)" 
//           }}
//         >
//           {step === "email" 
//             ? (sendingOtp ? "Sending..." : "Continue with Email") 
//             : (verifyingOtp ? "Verifying..." : "Verify & Login")
//           }
//         </button>

//       </GlassCard>
//     </AuthLayout>
//   );
// }
//************************************************************************************************ */
// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail, Lock } from "lucide-react"; // Added Lock icon for OTP
// import { useNavigate, Link } from "react-router-dom"; // Moved Link here
// import { GoogleLogin } from "@react-oauth/google";

// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// // AuthHeader is removed from the return to build the custom Figma header
// import { BASE_URL } from "./config/api";

// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email");

//   const [sendingOtp, setSendingOtp] = useState(false);
//   const [verifyingOtp, setVerifyingOtp] = useState(false);
//   const [googleLoading, setGoogleLoading] = useState(false);

//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   /* ---------------- TIMER ---------------- */
//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   /* ---------------- SEND OTP ---------------- */
//   const sendOtp = async (isResend = false) => {
//     if (!isValidEmail(email)) {
//       setError("Enter a valid email address");
//       return;
//     }

//     try {
//       setSendingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/send-email-otp`,
//         { email }
//       );

//       if (res.data?.status) {
//         setStep("otp");
//         setOtp("");
//         setCooldown(59);
//       } else {
//         setError(res.data?.message || "Failed to send OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setSendingOtp(false);
//     }
//   };

//   /* ---------------- VERIFY OTP ---------------- */
//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) {
//       setError("Enter valid OTP");
//       return;
//     }

//     try {
//       setVerifyingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/verify-email-otp`,
//         { email, otp }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Invalid OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally {
//       setVerifyingOtp(false);
//     }
//   };

//   /* ---------------- GOOGLE LOGIN ---------------- */
//   const handleGoogleLogin = async (credentialResponse) => {
//     try {
//       setGoogleLoading(true);
//       setError("");

//       const token = credentialResponse?.credential;
//       if (!token) {
//         setError("Google token missing");
//         return;
//       }

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/auth/google`,
//         { token }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Google login failed");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Google login failed");
//     } finally {
//       setGoogleLoading(false);
//     }
//   };

//   return (
//     <AuthLayout>
//       <GlassCard>
        
//         {/* FIGMA STYLE LOGO AND HEADER */}
//         <div className="flex flex-col items-center mb-6">
//           <div className="w-14 h-14 bg-gradient-to-br from-[#16a34a] to-[#15803d] text-white flex items-center justify-center text-3xl font-black rounded-2xl mb-3 shadow-lg transform -rotate-3">
//             <span className="rotate-3">G</span>
//           </div>
//           <h2 className="text-2xl font-extrabold text-slate-900">Welcome Back!</h2>
//           <p className="text-[11px] text-slate-500 font-medium mt-1">
//             Let's login for explore continues
//           </p>
//         </div>

//         {/* EMAIL INPUT */}
//         <div className="mb-4">
//           <label className="block text-[11px] font-bold text-slate-700 mb-1.5 ml-1">
//             Email Address
//           </label>
//           <div className="relative flex items-center border border-slate-200 rounded-xl bg-slate-50 px-4 focus-within:border-[#15803d] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all">
//             <Mail className="text-slate-400 mr-3" size={18} />
//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="Enter your email"
//               className="w-full bg-transparent py-3.5 text-sm font-medium outline-none placeholder:text-slate-400 disabled:opacity-60 text-slate-800"
//             />
//           </div>
//         </div>

//         {/* OTP INPUT */}
//         {step === "otp" && (
//           <div className="mb-4">
//             <label className="block text-[11px] font-bold text-slate-700 mb-1.5 ml-1">
//               Enter OTP
//             </label>
//             <div className="relative flex items-center border border-slate-200 rounded-xl bg-slate-50 px-4 focus-within:border-[#15803d] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#15803d]/10 transition-all">
//               <Lock className="text-slate-400 mr-3" size={18} />
//               <input
//                 value={otp}
//                 onChange={(e) =>
//                   setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
//                 }
//                 placeholder="6-digit OTP"
//                 className="w-full bg-transparent py-3.5 text-sm font-medium outline-none placeholder:text-slate-400 text-slate-800 tracking-[0.2em]"
//               />
//             </div>

//             {/* FIGMA STYLE ACTIONS */}
//             <div className="flex justify-between items-center mt-3 mx-1">
//               <button
//                 onClick={() => {
//                   setStep("email");
//                   setOtp("");
//                 }}
//                 className="text-[11px] font-bold text-slate-500 hover:text-[#15803d] transition"
//               >
//                 ← Edit Email
//               </button>

//               <button
//                 onClick={() => sendOtp(true)}
//                 disabled={cooldown > 0 || sendingOtp}
//                 className="text-[11px] font-bold text-[#16a34a] hover:underline disabled:opacity-50"
//               >
//                 {sendingOtp
//                   ? "Sending..."
//                   : cooldown > 0
//                   ? `Resend in 00:${String(cooldown).padStart(2, "0")}`
//                   : "Resend OTP"}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ERROR MESSAGE */}
//         {error && (
//           <p className="text-[11px] font-bold text-red-500 mb-4 text-center bg-red-50 py-2 rounded-lg">
//             {error}
//           </p>
//         )}

//         {/* PRIMARY BUTTON (Kept your exact styling) */}
//         <button
//           onClick={step === "email" ? () => sendOtp(false) : verifyOtp}
//           disabled={sendingOtp || verifyingOtp}
//           className="w-full rounded-xl py-3.5 font-bold text-[14px] text-white transition disabled:opacity-60 shadow-lg mt-2"
//           style={{ 
//             background: "linear-gradient(135deg, #16a34a, #22c55e)", 
//             boxShadow: "0 4px 20px rgba(34,197,94,0.35)" 
//           }}
//         >
//           {step === "email" 
//             ? (sendingOtp ? "Sending..." : "Sign in") 
//             : (verifyingOtp ? "Verifying..." : "Verify & Login")
//           }
//         </button>

      


//       </GlassCard>
//     </AuthLayout>
//   );
// }
//************************************************************************************************ */
// import React, { useEffect, useState, useContext } from "react";
// import axios from "axios";
// import { Mail } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { GoogleLogin } from "@react-oauth/google";

// import { AuthContext } from "./context/AuthContext";
// import AuthLayout from "./Auth/AuthLayout";
// import GlassCard from "./Auth/GlassCard";
// import AuthHeader from "./Auth/AuthHeader";
// import { BASE_URL } from "./config/api";
// import { Link } from "react-router-dom";

// export default function CustomerLogin() {
//   const navigate = useNavigate();
//   const { login } = useContext(AuthContext);

//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [step, setStep] = useState("email");

//   const [sendingOtp, setSendingOtp] = useState(false);
//   const [verifyingOtp, setVerifyingOtp] = useState(false);
//   const [googleLoading, setGoogleLoading] = useState(false);

//   const [cooldown, setCooldown] = useState(0);
//   const [error, setError] = useState("");

//   /* ---------------- TIMER ---------------- */
//   useEffect(() => {
//     if (cooldown <= 0) return;
//     const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
//     return () => clearInterval(timer);
//   }, [cooldown]);

//   const isValidEmail = (email) =>
//     /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);

//   /* ---------------- SEND OTP ---------------- */
//   const sendOtp = async (isResend = false) => {
//     if (!isValidEmail(email)) {
//       setError("Enter a valid email address");
//       return;
//     }

//     try {
//       setSendingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/send-email-otp`,
//         { email }
//       );

//       if (res.data?.status) {
//         setStep("otp");
//         setOtp("");
//         setCooldown(59);
//       } else {
//         setError(res.data?.message || "Failed to send OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setSendingOtp(false);
//     }
//   };

//   /* ---------------- VERIFY OTP ---------------- */
//   const verifyOtp = async () => {
//     if (!otp || otp.length < 4) {
//       setError("Enter valid OTP");
//       return;
//     }

//     try {
//       setVerifyingOtp(true);
//       setError("");

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/verify-email-otp`,
//         { email, otp }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Invalid OTP");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "OTP verification failed");
//     } finally {
//       setVerifyingOtp(false);
//     }
//   };

//   /* ---------------- GOOGLE LOGIN ---------------- */
//   const handleGoogleLogin = async (credentialResponse) => {
//     try {
//       setGoogleLoading(true);
//       setError("");

//       const token = credentialResponse?.credential;
//       if (!token) {
//         setError("Google token missing");
//         return;
//       }

//       const res = await axios.post(
//         `${BASE_URL}warranty/customer/auth/google`,
//         { token }
//       );

//       if (res.data?.status) {
//         login({ ...res.data.data, role: 7 });
//         navigate("/cust");
//       } else {
//         setError(res.data?.message || "Google login failed");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Google login failed");
//     } finally {
//       setGoogleLoading(false);
//     }
//   };

//   return (
//     <AuthLayout>
//       <GlassCard>
//         <AuthHeader
//           title="Customer Login"
//           subtitle="Sign in to manage your warranties"
//         />

//         {/* EMAIL */}
//         <div className="mb-4">
//           <label className="text-sm font-medium text-slate-700">
//             Email Address
//           </label>

//           <div className="relative rounded-xl bg-white/70 backdrop-blur border border-slate-200 px-3 mt-1 focus-within:ring-2 focus-within:ring-[#15803d] focus-within:border-[#15803d] transition">
//             <Mail className="absolute left-3 top-3 text-[#16a34a]" size={16} />

//             <input
//               value={email}
//               disabled={step === "otp"}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="you@example.com"
//               className="w-full bg-transparent pl-6 py-2 text-sm outline-none disabled:opacity-60"
//             />
//           </div>
//         </div>

//         {/* OTP */}
//         {step === "otp" && (
//           <div className="mb-4">
//             <label className="text-sm font-medium text-slate-700">
//               Enter OTP
//             </label>

//             <input
//               value={otp}
//               onChange={(e) =>
//                 setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
//               }
//               placeholder="6-digit OTP"
//               className="w-full mt-1 rounded-xl bg-white/70 backdrop-blur border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#15803d] focus:border-[#15803d] transition"
//             />

//             {/* RESEND */}
//             <div className="flex justify-end mt-2">
//               <button
//                 onClick={() => sendOtp(true)}
//                 disabled={cooldown > 0 || sendingOtp}
//                 className="text-xs text-[#15803d] font-medium hover:underline disabled:opacity-50"
//               >
//                 {sendingOtp
//                   ? "Sending..."
//                   : cooldown > 0
//                   ? `Resend in ${String(Math.floor(cooldown / 60)).padStart(2, "0")}:${String(cooldown % 60).padStart(2, "0")}`
//                   : "Resend OTP"}
//               </button>
//             </div>

//             {/* CHANGE EMAIL */}
//             <div className="flex justify-start mt-1">
//               <button
//                 onClick={() => {
//                   setStep("email");
//                   setOtp("");
//                 }}
//                 className="text-xs text-slate-500 hover:text-[#15803d] hover:underline transition"
//               >
//                 ← Change Email
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ERROR */}
//         {error && (
//           <p className="text-xs text-red-500 mb-3 text-center">{error}</p>
//         )}

//         {/* BUTTON */}
//         {step === "email" ? (
//           <button
//             onClick={() => sendOtp(false)}
//             disabled={sendingOtp}
//             className="w-full rounded-xl py-2.5 font-semibold text-white transition disabled:opacity-60"
//             style={{ background: "linear-gradient(135deg, #16a34a, #22c55e)", fontFamily: '"Sora", sans-serif', boxShadow: "0 4px 20px rgba(34,197,94,0.35)" }}
//           >
//             {sendingOtp ? "Sending..." : "Send OTP"}
//           </button>
//         ) : (
//           <button
//             onClick={verifyOtp}
//             disabled={verifyingOtp}
//             className="w-full rounded-xl py-2.5 font-semibold text-white transition disabled:opacity-60"
//             style={{ background: "linear-gradient(135deg, #16a34a, #22c55e)", fontFamily: '"Sora", sans-serif', boxShadow: "0 4px 20px rgba(34,197,94,0.35)" }}
//           >
//             {verifyingOtp ? "Verifying..." : "Verify & Login"}
//           </button>
//         )}

//         {/* DIVIDER */}
//         {/* {step === "email" && (
//           <>
//             <div className="flex items-center gap-3 my-5">
//               <div className="flex-1 h-px bg-slate-200" />
//               <span className="text-xs text-slate-400">OR</span>
//               <div className="flex-1 h-px bg-slate-200" />
//             </div>

//             <div className="flex justify-center">
//               <GoogleLogin
//                 onSuccess={handleGoogleLogin}
//                 onError={() => setError("Google login failed")}
//                 shape="pill"
//               />
//             </div>
//           </>
//         )} */}

//          {/* <div className="flex items-center gap-3 my-5">
//               <div className="flex-1 h-px bg-slate-200" />
//               <span className="text-xs text-slate-400">OR</span>
//               <div className="flex-1 h-px bg-slate-200" />
//             </div>

//             <div className="flex justify-center">
//               <Link to="/signup" className="text-blue-700 decoration text-xs border-b border-blue-700 hover:text-">Create new Account?</Link>
//             </div> */}

//         <p className="mt-6 text-center text-xs text-slate-400">
//           © {new Date().getFullYear()} Goelectronix
//         </p>
//       </GlassCard>
//     </AuthLayout>
//   );
// }